import React, { useMemo, useState } from 'react';

import StarIcon from '@material-ui/icons/Star';
import CloudOffIcon from "@material-ui/icons/CheckCircle";

import classNames from 'classnames';

import { LibraryItem } from '../../../../entities/LibraryItem';
import DateHelper from '../../../../utilities/DateHelper';
import { DisplayTextHelper } from '../../../../utilities/displayTextHelper';
import { LibraryItemType } from '../../../../entities/LibraryItemType';
import { useHistory } from 'react-router-dom';
import { routes } from '../../../../routes';
import { LinkAssetType } from '../../../../entities/LinkAssetType';
import { FileAssetService } from '../../../../services/FIleAssetService';
import { ShowEmbeddedLinkDialog } from './ShowEmbeddedLinkDialog';
import { LibraryAssetActions } from '../../LibraryAssetActions/LibraryAssetActions';
import { LibraryItemIcon } from './LibraryItemIcon';
import { SortCatagories } from './SortByDropDown';
import { TableRow, TableCell, Popover, Paper, ClickAwayListener, makeStyles } from '@material-ui/core';
import { LoadingDialog } from '../../../../components/LoadingDialog';

const useStyles = makeStyles((theme) => ({
    tableRow: {
        cursor: "context-menu",
        paddingRight: 56,
        paddingLeft: 24
    },
    focusedBlue: {
        color: "#1967d2 !important"
    },
    tableText: {
        color: "#5f6368",
        fontSize: "13px",
        fontWeight: 500,
        fontFamily: "Roboto, Helvetica",
    },
    selected: {
        backgroundColor: `${theme.palette.primary.main} !important`,
        "&:hover": {
            backgroundColor: "#4d90fe"
        },
        "& span": {
            color: "#FFF"
        }
    },
    iconText: {
        verticalAlign: "super"
    },
    inlineIcon: {
        color: "rgb(143, 143, 143)",
        fontSize: "16px",
        verticalAlign: "text-bottom",
        marginLeft: "4px"
    },
}));

interface Props {
    item: LibraryItem;
    sortBy?: SortCatagories;
    focusedItemId?: string;
    setFocusedItem: (id:string) => void;
}

export function DesktopLibraryTableRow(props:Props) {
    const { item, sortBy, focusedItemId} = props;
    const history = useHistory();
    const classes = useStyles();

    const {fromAssetCategory, fromLibraryItemType} = DisplayTextHelper;
    const { buildDateFormat } = DateHelper;

    const [downloading, setDownloading] = useState(false)
    const [showPopover, setShowPopover] = useState(false);
    const [popoverLeft, setPopoverLeft] = useState(0);
    const [popoverTop, setPopoverTop] = useState(0);

    const [showEmbeddedLinkDialog, setShowEmbeddedLinkDialog] = useState(false);

    const hasFocus = useMemo(() => focusedItemId === item.id, [focusedItemId, item])
    const changeColumnData = useMemo(() => sortBy !== "Name" && sortBy !== "Type", [sortBy]);
    const isFolderType = useMemo(() => 
        item.type === LibraryItemType.Folder || item.type === LibraryItemType.SermonSeries ||
        item.type === LibraryItemType.PremiumSermon || item.type === LibraryItemType.PremiumSermonSeries
    , [item]);

    const handleRightClick = (e:React.MouseEvent<HTMLTableRowElement>) => {
        e.preventDefault();
        props.setFocusedItem(item.id);
        setPopoverLeft(e.clientX)
        setPopoverTop(e.clientY)
        setShowPopover(true);
    }

    const handleDoubleClick = async() => {
        switch (item.type) {
			case LibraryItemType.BuilderAsset:
				return history.push(routes.app.resolve.builderPage(item.id), { prevUrl: window.location.href });

			case LibraryItemType.LinkAsset: {
				if (item.linkType! === LinkAssetType.General && item.linkUrl != null) {
					const redirectUrl = item.linkUrl.match(/^https?:\/\//i) ? item.linkUrl : "//" + item.linkUrl;
					return window.open(redirectUrl, "_blank");
				} else {
					return setShowEmbeddedLinkDialog(true)
				}
			}

			case LibraryItemType.FileAsset: {
                setDownloading(true);                
                await FileAssetService.downloadFile(item.id, item.name)
                setDownloading(false);   
                return;             
            }

			default:
                return history.push(routes.app.resolve.libraryFolder(item.id))
		}
    }

    return (
        <>
            <TableRow
                style={{ backgroundColor: hasFocus ? "#e8f0fe" : undefined}}
                key={item.name}
                selected={hasFocus}
                onClick={() => props.setFocusedItem(item.id)}
                onDoubleClick={handleDoubleClick}
                onContextMenu={handleRightClick}
                className={classes.tableRow}
            >
                <TableCell style={{ paddingRight: 0, paddingTop: 4, paddingBottom: 4 }}>
                    <>
                        <span>
                            <LibraryItemIcon itemType={item.type} isTemplate={item.isTemplate} linkType={item.linkType} />
                            <span
                                className={classNames(classes.tableText, classes.iconText, hasFocus && classes.focusedBlue)}
                                >
                                {item.name} {item.isTemplate && <> - <strong>Custom Template</strong></>} 
                                {item.isStarred && <StarIcon className={classNames(classes.inlineIcon, hasFocus && classes.focusedBlue)} />}
                                {item.offlineMode && <CloudOffIcon className={classNames(classes.inlineIcon, hasFocus && classes.focusedBlue)} />}
                            </span>
                        </span>
                    </>
                </TableCell>
                <TableCell>
                    <span className={classNames(classes.tableText, hasFocus && classes.focusedBlue)}>
                        {isFolderType ? fromLibraryItemType(item.type) : fromAssetCategory(item.category!)}
                    </span>
                </TableCell>
                <TableCell>
                    <span className={classNames(classes.tableText, hasFocus && classes.focusedBlue)}>
                        {sortBy === "Created Date" && buildDateFormat(item.dateCreated)}
                        {sortBy === "Preach Date" && item.datePreached ? buildDateFormat(item.datePreached) : null}
                        {sortBy === "Last Opened" && buildDateFormat(item.dateAccessed)}
                        {sortBy === "Last Modified" && buildDateFormat(item.dateModified)}
                        {sortBy === "Location" && item.locationPreached ? item.locationPreached : null}
                        {!changeColumnData && buildDateFormat(item.dateModified)}
                    </span>
                </TableCell>
            </TableRow>
            { item.type === LibraryItemType.LinkAsset &&
                <ShowEmbeddedLinkDialog 
                    open={showEmbeddedLinkDialog} 
                    onClose={() => setShowEmbeddedLinkDialog(false)}
                    linkUrl={item.linkUrl!}
                    linkType={item.linkType!}
                />
            }
            {
                showPopover &&
                <Popover
                open={showPopover}
                anchorPosition={{ top: popoverTop, left: popoverLeft + 70 }}
                anchorReference="anchorPosition"
                onClose={() => setShowPopover(false)}
                anchorOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'center',
                    horizontal: 'center',
                }}
            >
                <Paper elevation={2}>
                    <ClickAwayListener mouseEvent="onMouseUp" onClickAway={(e) => {
                        const mouseEvent = e;
                        if(mouseEvent.button === 2) return setShowPopover(false)
                    }}>
                        <LibraryAssetActions focusedItem={item} onClose={() => setShowPopover(false)} />
                    </ClickAwayListener>
                </Paper>
            </Popover>}
            { downloading && <LoadingDialog text='Downloading' />}
        </>
    );
}